import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
} from '@mui/material';
import React from 'react';

import {
  ClassChip,
  formatDate,
  type StoredScreeningProfile,
} from './screening-details-drawer';

import useSnackbarContext from '~/client/components/base/snackbar/snackbar-context';
import * as GQL from '~/graphql/generated/client';
import Button from '~c/components/base/button';
import Text from '~c/components/base/text';

const TextLink: React.FC<React.PropsWithChildren<{ onClick: () => void }>> = ({
  onClick,
  children,
}) => (
  <Link
    component="button"
    underline="hover"
    onClick={onClick}
    sx={{ fontSize: 'inherit', fontWeight: 600, verticalAlign: 'baseline' }}
  >
    {children}
  </Link>
);

interface Props {
  projectId: string;
  profile: StoredScreeningProfile | null;
  canManage: boolean;
  onChanged: () => void;
  onOpenDetails: () => void;
}

const ScreeningOutcomePanel: React.FC<Props> = ({
  projectId,
  profile,
  canManage,
  onChanged,
  onOpenDetails,
}) => {
  const Snackbar = useSnackbarContext();
  const [rerunOpen, setRerunOpen] = React.useState(false);
  const [rerunReason, setRerunReason] = React.useState('');
  const [reevaluate, { loading: reevaluating }] =
    GQL.useReevaluateProjectClassificationMutation();

  if (!profile?.classification) {
    return null;
  }

  const answersChanged =
    profile.answersChangedAt != null &&
    (!profile.evaluatedAt ||
      new Date(profile.answersChangedAt) > new Date(profile.evaluatedAt));
  const classifications = profile.classifications ?? [];
  const flags = profile.flags ?? [];
  const history = profile.history ?? [];
  const lastRunBy = history[history.length - 1]?.evaluatedByName;

  const handleRerun = async () => {
    try {
      await reevaluate({
        variables: { projectId, reason: rerunReason.trim() },
      });
      Snackbar.open('Classification re-run.', { showOk: true });
      setRerunOpen(false);
      setRerunReason('');
      onChanged();
    } catch (err) {
      Snackbar.open(
        err instanceof Error ? err.message : 'Failed to re-run classification'
      );
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
            {classifications.map((c) => (
              <Stack key={c.key} direction="row" alignItems="center" gap={1}>
                <Text sz="bold-body14">{c.label}</Text>
                <ClassChip label={c.classLabel} color={c.color} />
              </Stack>
            ))}
            {classifications.length === 0 && (
              <Text sz="bold-body14">
                Classification: {profile.classification}
              </Text>
            )}
          </Stack>
          {flags.length > 0 && (
            <Text sz="body14">
              Flags: {flags.map((f) => f.label).join(' · ')}
            </Text>
          )}
          <Text sz="body14" color="text.secondary">
            Evaluated {formatDate(profile.evaluatedAt)}
            {lastRunBy ? ` by ${lastRunBy}` : ''} ·{' '}
            <TextLink onClick={onOpenDetails}>View details</TextLink>
            {canManage && (
              <>
                {' · '}
                <TextLink onClick={() => setRerunOpen(true)}>Re-run</TextLink>
              </>
            )}
          </Text>
        </Stack>
      </Box>

      {answersChanged && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Screening answers changed since the last classification.
          {canManage && (
            <>
              {' '}
              <TextLink onClick={() => setRerunOpen(true)}>Re-run now</TextLink>
            </>
          )}
        </Alert>
      )}

      <Dialog
        open={rerunOpen}
        onClose={() => setRerunOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Re-run classification</DialogTitle>
        <DialogContent>
          <Text sz="body14" color="text.secondary" sx={{ mb: 2 }}>
            The current screening answers are checked against the active rules.
            The previous result is kept in the history.
          </Text>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason for re-classification"
            placeholder="e.g. Model retrained, new EU deployment"
            value={rerunReason}
            onChange={(e) => setRerunReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            outlined
            onClick={() => setRerunOpen(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={reevaluating || rerunReason.trim() === ''}
            onClick={handleRerun}
          >
            Re-run
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreeningOutcomePanel;
